import { DatePicker, Modal, Space, message } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import moment from "moment";
import { formatDate } from "utils/dateFormat";
import { getOrderItemCompleteReport } from "services/factoryAPI";
import type { OrderItemCompleteRow } from "services/interface";
import { numberFormat2 } from "utils/nuberFormat";

/** รองรับ body แบบ { data: [] } หรือ [] ตรงๆ หรือ { data: { data: [] } } */
function normalizeOrderItemCompleteRows(
  res: unknown
): OrderItemCompleteRow[] {
  if (Array.isArray(res)) {
    return res as OrderItemCompleteRow[];
  }
  if (!res || typeof res !== "object") {
    return [];
  }
  const top = res as { data?: unknown };
  if (Array.isArray(top.data)) {
    return top.data as OrderItemCompleteRow[];
  }
  if (
    top.data &&
    typeof top.data === "object" &&
    Array.isArray((top.data as { data?: unknown }).data)
  ) {
    return (top.data as { data: OrderItemCompleteRow[] }).data;
  }
  return [];
}

interface ModalExportOrderItemCompleteProps {
  open: boolean;
  onClose: () => void;
  factoryName: string;
}

const ModalExportOrderItemComplete: FC<ModalExportOrderItemCompleteProps> = ({
  open,
  onClose,
  factoryName,
}) => {
  const [start_date_input, setStartDateInput] = useState<string>("");
  const [end_date_input, setEndDateInput] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setStartDateInput("");
      setEndDateInput("");
    }
  }, [open]);

  const handleExport = async (): Promise<boolean | void> => {
    if (!start_date_input || !end_date_input) {
      message.warning("กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด");
      // Ant Design Modal: คืน false = ไม่ปิด Modal, ไม่ reject เพื่อเลี่ยง Uncaught (in promise)
      return false;
    }
    if (dayjs(end_date_input).isBefore(dayjs(start_date_input), "day")) {
      message.warning("วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น");
      return false;
    }
    const start_date = start_date_input;
    const end_date = end_date_input;
    setLoading(true);
    try {
      const res = await getOrderItemCompleteReport({ start_date, end_date });
      const rows = normalizeOrderItemCompleteRows(res);
      if (!rows.length) {
        message.warning("ไม่มีข้อมูลในช่วงที่เลือก");
        return false;
      }
      const arr = rows.map((item) => ({
        หมายเลขคำสั่งซื้อ: item.order_code,
        ชื่อสินค้า: item.product_name,
        ตัวเลือกสินค้า: item.sku_name,
        จำนวน: item.item_qty,
        ราคาโอนให้โรงงาน: numberFormat2(item.base_factory_price || 0),
        ราคารวม: numberFormat2(item.base_factory_price * item.item_qty || 0),
        วันที่สั่งซื้อ: formatDate(item.create_date) || "-",
      }));

      const Heading = [
        [
          "หมายเลขคำสั่งซื้อ",
          "ชื่อสินค้า",
          "ตัวเลือกสินค้า",
          "จำนวน",
          "ราคาโอนให้โรงงาน",
          "ราคารวม",
          "วันที่สั่งซื้อ",
        ],
      ];

      const rowFactoryName = `ชื่อโรงงาน: ${factoryName || "-"}`;
      const rowStart = `วันที่เริ่มต้น (export): ${dayjs(start_date).format("DD/M/YYYY")}`;
      const rowEnd = `วันที่สิ้นสุด (export): ${dayjs(end_date).format("DD/M/YYYY")}`;

      const wb = XLSX.utils.book_new();
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);

      XLSX.utils.sheet_add_aoa(ws, [[rowFactoryName]], { origin: "A1" });
      XLSX.utils.sheet_add_aoa(ws, [[rowStart]], { origin: "A2" });
      XLSX.utils.sheet_add_aoa(ws, [[rowEnd]], { origin: "A3" });
      XLSX.utils.sheet_add_aoa(ws, Heading, { origin: "A5" });
      XLSX.utils.sheet_add_json(ws, arr, { origin: "A6", skipHeader: true });

      XLSX.utils.book_append_sheet(wb, ws, "รายการคำสั่งซื้อ");

      const fileName = `order_item_${moment().format("DD-MM-YYYY-HHmmss")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success("ส่งออกไฟล์สำเร็จ");
      onClose();
    } catch {
      message.error("ไม่สามารถส่งออกไฟล์ได้");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="ส่งออก Excel — รายการคำสั่งซื้อ"
      open={open}
      onCancel={onClose}
      onOk={handleExport}
      okText="ส่งออก Excel"
      cancelText="ยกเลิก"
      confirmLoading={loading}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <span>วันที่เริ่มต้น</span>
        <DatePicker
          style={{ width: "100%" }}
          format="DD/M/YYYY"
          placeholder="วันที่เริ่มต้น"
          value={start_date_input ? dayjs(start_date_input) : null}
          onChange={(d) =>
            setStartDateInput(d ? d.format("YYYY-MM-DD") : "")
          }
          placement="bottomLeft"
        />
        <span>วันที่สิ้นสุด</span>
        <DatePicker
          style={{ width: "100%" }}
          format="DD/M/YYYY"
          placeholder="วันที่สิ้นสุด"
          value={end_date_input ? dayjs(end_date_input) : null}
          onChange={(d) =>
            setEndDateInput(d ? d.format("YYYY-MM-DD") : "")
          }
          placement="bottomLeft"
        />
      </Space>
    </Modal>
  );
};

export default ModalExportOrderItemComplete;
